import React from 'react';
import {
  CompassOutlined,
  DashboardOutlined,
  LineChartOutlined,
  UserOutlined,
  BugOutlined,
  BuildOutlined,
  ApiOutlined,
  BarcodeOutlined,
  ExceptionOutlined,
  CheckSquareOutlined,
  ScheduleOutlined,
  WarningOutlined,
  FieldTimeOutlined,
  ShopOutlined,
  ExclamationCircleOutlined,
  AppstoreOutlined,
  ExperimentOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  canCreateDevice,
  canCalibrateDevice,
  canRepairDevice,
  canReportIncident,
  canStopUsingDevice,
  canCancelDevice,
  canManageData,
  canUpdateCertificate,
} from '@app/services/localStorage.service';
import { canCreateIdea, canExportReport, canViewMyTasks } from '@app/constants/kaizen-permissions';

export interface SidebarNavigationItem {
  title: string;
  key: string;
  url?: string;
  children?: SidebarNavigationItem[];
  icon?: React.ReactNode;
  isRoot: boolean;
}

const sidebarWH = [
  {
    title: 'common.gemba-dashboard',
    key: 'index',
    url: '/wh',
    icon: <DashboardOutlined />,
    isRoot: false,
  },
  {
    title: 'common.issues',
    key: 'forms',
    icon: <BugOutlined />,
    children: [
      {
        title: 'issuepage.createUpdateIssue',
        key: 'issue-forms',
        url: '/wh/issue/info',
        isRoot: false,
      },
      {
        title: 'common.queryissue',
        key: 'issue-query',
        url: '/wh/issue/query',
        isRoot: false,
      },
      {
        title: 'common.handleIssue',
        key: 'issue-handle',
        url: '/wh/issue/handle',
        isRoot: false,
      },
    ],
    isRoot: false,
  },
  {
    title: 'common.charts',
    key: 'charts',
    url: '/wh/charts',
    icon: <LineChartOutlined />,
    isRoot: false,
  },
  {
    title: 'common.usersManagement',
    key: 'usermanagement',
    url: '/wh/users',
    icon: <UserOutlined />,
    isRoot: true,
  },
  {
    title: 'common.stagesManagement',
    key: 'stagemanagement',
    url: '/wh/stages',
    icon: <BuildOutlined />,
    isRoot: true,
  },
  {
    title: 'common.equipmentManagement',
    key: 'equipments',
    url: '/wh/equipments',
    icon: <ApiOutlined />,
    isRoot: false,
  },
  {
    title: 'common.productManagement',
    key: 'products',
    url: '/wh/products',
    icon: <BarcodeOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.supplyManagement',
    key: 'supply',
    url: '/wh/supply',
    icon: <ShopOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCodeManagement',
    key: 'error-code',
    url: '/wh/error-code',
    icon: <WarningOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCategoryManagement',
    key: 'error-category',
    url: '/wh/error-category',
    icon: <ExclamationCircleOutlined />,
    isRoot: true,
  },
];

const sidebarCBM = [
  {
    title: 'cbm.common.gemba-dashboard',
    key: 'index',
    url: '/cbm',
    icon: <DashboardOutlined />,
    isRoot: false,
  },
  {
    title: 'cbm.common.issues',
    key: 'forms',
    icon: <BugOutlined />,
    children: [
      {
        title: 'cbm.issuepage.createUpdateIssue',
        key: 'issue-forms',
        url: '/cbm/issue/info',
        isRoot: false,
      },
      {
        title: 'cbm.common.queryissue',
        key: 'issue-query',
        url: '/cbm/issue/query',
        isRoot: false,
      },
      {
        title: 'cbm.common.mytasks',
        key: 'issue-my-tasks',
        url: '/cbm/issue/my-tasks',
        isRoot: false,
      },
    ],
    isRoot: false,
  },

  {
    title: 'cbm.common.charts',
    key: 'charts',
    url: '/cbm/charts',
    icon: <LineChartOutlined />,
    isRoot: false,
  },
  {
    title: 'cbm.common.usersManagement',
    key: 'usermanagement',
    url: '/cbm/users',
    icon: <UserOutlined />,
    isRoot: true,
  },
  {
    title: 'cbm.common.stagesManagement',
    key: 'stagemanagement',
    url: '/cbm/stages',
    icon: <BuildOutlined />,
    isRoot: true,
  },
  {
    title: 'cbm.common.equipmentManagement',
    key: 'equipments',
    url: '/cbm/equipments',
    icon: <ApiOutlined />,
    isRoot: false,
  },
  {
    title: 'cbm.common.productManagement',
    key: 'products',
    url: '/cbm/products',
    icon: <BarcodeOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.supplyManagement',
    key: 'supply',
    url: '/cbm/supply',
    icon: <ShopOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCodeManagement',
    key: 'error-code',
    url: '/cbm/error-code',
    icon: <WarningOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCategoryManagement',
    key: 'error-category',
    url: '/cbm/error-category',
    icon: <ExclamationCircleOutlined />,
    isRoot: true,
  },
];

const sidebarIQC = [
  {
    title: 'iqc.common.gemba-dashboard',
    key: 'index',
    url: '/iqc',
    icon: <DashboardOutlined />,
    isRoot: false,
  },
  {
    title: 'iqc.common.issues',
    key: 'forms',
    icon: <BugOutlined />,
    children: [
      {
        title: 'iqc.issuepage.createUpdateIssue',
        key: 'issue-forms',
        url: '/iqc/issue/info',
        isRoot: false,
      },
      {
        title: 'iqc.common.queryissue',
        key: 'issue-query',
        url: '/iqc/issue/query',
        isRoot: false,
      },
      // {
      //   title: 'iqc.common.handleIssue',
      //   key: 'issue-handle',
      //   url: '/iqc/issue/handle',
      //   isRoot: false,
      // },
    ],
    isRoot: false,
  },
  {
    title: 'iqc.common.charts',
    key: 'charts',
    url: '/iqc/charts',
    icon: <LineChartOutlined />,
    isRoot: false,
  },
  {
    title: 'iqc.common.usersManagement',
    key: 'usermanagement',
    url: '/iqc/users',
    icon: <UserOutlined />,
    isRoot: true,
  },
  {
    title: 'iqc.common.stagesManagement',
    key: 'stagemanagement',
    url: '/iqc/stages',
    icon: <BuildOutlined />,
    isRoot: true,
  },
  {
    title: 'iqc.common.equipmentManagement',
    key: 'equipments',
    url: '/iqc/equipments',
    icon: <ApiOutlined />,
    isRoot: false,
  },
  {
    title: 'iqc.common.productManagement',
    key: 'products',
    url: '/iqc/products',
    icon: <BarcodeOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.supplyManagement',
    key: 'supply',
    url: '/iqc/supply',
    icon: <ShopOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCodeManagement',
    key: 'error-code',
    url: '/iqc/error-code',
    icon: <WarningOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCategoryManagement',
    key: 'error-category',
    url: '/iqc/error-category',
    icon: <ExclamationCircleOutlined />,
    isRoot: true,
  },
];

const sidebarFac = [
  {
    title: 'fac.common.masterList',
    key: 'index',
    url: 'fac',
    icon: <DashboardOutlined />,
    isRoot: false,
  },
  {
    title: 'fac.common.checklist',
    key: 'check-list-forms',
    icon: <CheckSquareOutlined />,
    children: [
      {
        title: 'fac.checkListPage.managementCheckList',
        key: 'check-list',
        url: 'fac/check-list',
        isRoot: false,
      },
      {
        title: 'fac.checkListPage.createUpdateCheckList',
        key: 'check-list-forms',
        url: 'fac/check-list/info',
        isRoot: false,
      },
      {
        title: 'fac.common.queryChecklist',
        key: 'check-list-query',
        url: 'fac/check-list/query',
        isRoot: false,
      },
    ],
    isRoot: false,
  },
  {
    title: 'fac.common.issues',
    key: 'issue-forms',
    icon: <BugOutlined />,
    children: [
      {
        title: 'fac.issuepage.managementIssue',
        key: 'issue',
        url: 'fac/issue',
        isRoot: false,
      },
      {
        title: 'fac.issuepage.createUpdateIssue',
        key: 'issue-forms',
        url: 'fac/issue/info',
        isRoot: false,
      },
      {
        title: 'fac.common.queryissue',
        key: 'issue-query',
        url: 'fac/issue/query',
        isRoot: false,
      },
    ],
    isRoot: false,
  },
  {
    title: 'fac.common.tasks',
    key: 'task-forms',
    icon: <ScheduleOutlined />,
    children: [
      {
        title: 'fac.taskPage.managementTask',
        key: 'task',
        url: 'fac/task',
        isRoot: false,
      },
      {
        title: 'fac.taskPage.createUpdateTask',
        key: 'task-forms',
        url: 'fac/task/info',
        isRoot: false,
      },
      {
        title: 'fac.common.querytask',
        key: 'task-query',
        url: 'fac/task/query',
        isRoot: false,
      },
    ],
    isRoot: false,
  },
  {
    title: 'fac.common.allIssue',
    key: 'allIssue',
    url: 'fac/all-issue',
    icon: <WarningOutlined />,
    isRoot: true,
  },
  {
    title: 'fac.common.charts',
    key: 'charts',
    url: 'fac/charts',
    icon: <LineChartOutlined />,
    isRoot: false,
  },
  {
    title: 'fac.common.shiftManagement',
    key: 'shiftmanagement',
    url: 'fac/shiftmanagement',
    icon: <FieldTimeOutlined />,
    isRoot: false,
  },
  {
    title: 'fac.common.usersManagement',
    key: 'usermanagement',
    url: 'fac/users',
    icon: <UserOutlined />,
    isRoot: true,
  },

  {
    title: 'fac.common.equipmentManagement',
    key: 'equipments',
    url: 'fac/equipments',
    icon: <ApiOutlined />,
    isRoot: false,
  },
];

const sidebarINT = [
  {
    title: 'int.common.gemba-dashboard',
    key: 'index',
    url: '/int',
    icon: <DashboardOutlined />,
    isRoot: false,
  },

  {
    title: 'int.common.issues',
    key: 'forms',
    icon: <BugOutlined />,
    children: [
      {
        title: 'int.issuepage.createUpdateIssue',
        key: 'issue-forms',
        url: '/int/issue/info',
        isRoot: false,
      },
      {
        title: 'int.common.queryissue',
        key: 'issue-query',
        url: '/int/issue/query',
        isRoot: false,
      },
    ],
    isRoot: false,
  },

  {
    title: 'int.common.charts',
    key: 'charts',
    url: '/int/charts',
    icon: <LineChartOutlined />,
    isRoot: false,
  },
  {
    title: 'int.common.usersManagement',
    key: 'usermanagement',
    url: '/int/users',
    icon: <UserOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.stagesManagement',
    key: 'stagemanagement',
    url: '/int/stages',
    icon: <BuildOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.equipmentManagement',
    key: 'equipments',
    url: '/int/equipments',
    icon: <ApiOutlined />,
    isRoot: false,
  },
  {
    title: 'int.common.productManagement',
    key: 'products',
    url: '/int/products',
    icon: <BarcodeOutlined />,
    isRoot: true,
  },

  {
    title: 'int.common.supplyManagement',
    key: 'supply',
    url: '/int/supply',
    icon: <ShopOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCodeManagement',
    key: 'error-code',
    url: '/int/error-code',
    icon: <WarningOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCategoryManagement',
    key: 'error-category',
    url: '/int/error-category',
    icon: <ExclamationCircleOutlined />,
    isRoot: true,
  },
];

const sidebarSPA = [
  {
    title: 'spa.common.gemba-dashboard',
    key: 'index',
    url: '/spa',
    icon: <DashboardOutlined />,
    isRoot: false,
  },
  {
    title: 'spa.common.issues',
    key: 'forms',
    icon: <BugOutlined />,
    children: [
      {
        title: 'spa.issuepage.createUpdateIssue',
        key: 'issue-forms',
        url: '/spa/issue/info',
        isRoot: false,
      },
      {
        title: 'spa.common.queryissue',
        key: 'issue-query',
        url: '/spa/issue/query',
        isRoot: false,
      },
    ],
    isRoot: false,
  },
  {
    title: 'spa.common.charts',
    key: 'charts',
    url: '/spa/charts',
    icon: <LineChartOutlined />,
    isRoot: false,
  },
  {
    title: 'spa.common.usersManagement',
    key: 'usermanagement',
    url: '/spa/users',
    icon: <UserOutlined />,
    isRoot: true,
  },
  // {
  //   title: 'spa.common.issueOwner',
  //   key: 'issueOwner',
  //   url: '/spa/issueOwner',
  //   icon: <CompassOutlined />,
  //   isRoot: true,
  // },
  {
    title: 'spa.common.stagesManagement',
    key: 'stagemanagement',
    url: '/spa/stages',
    icon: <BuildOutlined />,
    isRoot: true,
  },
  {
    title: 'spa.common.equipmentManagement',
    key: 'equipments',
    url: '/spa/equipments',
    icon: <ApiOutlined />,
    isRoot: false,
  },
  {
    title: 'spa.common.productManagement',
    key: 'products',
    url: '/spa/products',
    icon: <BarcodeOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.supplyManagement',
    key: 'supply',
    url: '/spa/supply',
    icon: <ShopOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCodeManagement',
    key: 'error-code',
    url: '/spa/error-code',
    icon: <WarningOutlined />,
    isRoot: true,
  },
  {
    title: 'int.common.errorCategoryManagement',
    key: 'error-category',
    url: '/spa/error-category',
    icon: <ExclamationCircleOutlined />,
    isRoot: true,
  },

  // {
  //   title: 'spa.common.qualityCodeManagement',
  //   key: 'qualityCode',
  //   url: '/spa/quality-codes',
  //   icon: <ExceptionOutlined />,
  //   isRoot: true,
  // },
];

/** Calib sidebar - Dashboard, Quick scan, Device info, Actions, Management */
const getSidebarCalib = (): SidebarNavigationItem[] => {
  const actionChildren: SidebarNavigationItem[] = [];
  if (canCreateDevice()) {
    actionChildren.push({ title: 'Tạo mới', key: 'create-new', url: '/calib/actions/create-new', isRoot: false });
  }
  if (canManageData()) {
    actionChildren.push({ title: 'Luân chuyển', key: 'transfer', url: '/calib/actions/transfer', isRoot: false });
  }
  if (canCalibrateDevice()) {
    actionChildren.push({ title: 'Hiệu chuẩn', key: 'calibration', url: '/calib/actions/calibration', isRoot: false });
  }
  if (canRepairDevice()) {
    actionChildren.push({ title: 'Sửa chữa', key: 'repair', url: '/calib/actions/repair', isRoot: false });
  }
  if (canReportIncident()) {
    actionChildren.push({
      title: 'Báo sự cố',
      key: 'report-incident',
      url: '/calib/actions/report-incident',
      isRoot: false,
    });
  }
  if (canStopUsingDevice()) {
    actionChildren.push({ title: 'Ngừng sử dụng', key: 'stop-using', url: '/calib/actions/stop-using', isRoot: false });
  }
  if (canCancelDevice()) {
    actionChildren.push({ title: 'Hủy', key: 'cancel', url: '/calib/actions/cancel', isRoot: false });
  }

  const sidebar: SidebarNavigationItem[] = [
    { title: 'Dashboard', key: 'dashboard', url: '/calib/dashboard', icon: <DashboardOutlined />, isRoot: false },
    { title: 'Quick scan', key: 'quick-scan', url: '/calib/quick-scan', icon: <BarcodeOutlined />, isRoot: false },
    {
      title: 'Thông tin thiết bị',
      key: 'device-info',
      url: '/calib/device-info',
      icon: <ApiOutlined />,
      isRoot: false,
    },
    {
      title: 'Thông tin công việc',
      key: 'task-info',
      url: '/calib/task-info',
      icon: <CheckSquareOutlined />,
      isRoot: false,
    },
  ];
  if (actionChildren.length > 0) {
    sidebar.push({
      title: 'Hành động',
      key: 'actions',
      icon: <BuildOutlined />,
      children: actionChildren,
      isRoot: false,
    });
  }
  sidebar.push(
    {
      title: 'Quản lý tài khoản',
      key: 'account-management',
      url: '/calib/account-management',
      icon: <UserOutlined />,
      isRoot: true,
    },
    {
      title: 'Quản lý vị trí sử dụng',
      key: 'location-management',
      url: '/calib/location-management',
      icon: <CompassOutlined />,
      isRoot: false,
    },
    {
      title: 'Quản lý danh mục thiết bị',
      key: 'category-management',
      url: '/calib/category-management',
      icon: <AppstoreOutlined />,
      isRoot: false,
    },
    {
      title: 'Quản lý đơn vị hiệu chuẩn',
      key: 'calibration-unit-management',
      url: '/calib/calibration-unit-management',
      icon: <ExperimentOutlined />,
      isRoot: false,
    },
    {
      title: 'Quản lý đơn vị sửa chữa',
      key: 'repair-unit-management',
      url: '/calib/repair-unit-management',
      icon: <ToolOutlined />,
      isRoot: false,
    },
  );
  if (canUpdateCertificate()) {
    sidebar.push({
      title: 'UPDATE CERTIFICATE',
      key: 'update-certificate',
      url: '/calib/update-certificate',
      icon: <SafetyCertificateOutlined />,
      isRoot: false,
    });
  }
  sidebar.push({
    title: 'Khuyến cáo thay thế',
    key: 'replacement-recommendation',
    url: '/calib/replacement-recommendation',
    icon: <SwapOutlined />,
    isRoot: false,
  });
  return sidebar;
};

const getSidebarKPI = (): SidebarNavigationItem[] => {
  const actionChildren: SidebarNavigationItem[] = [];

  if (canCreateIdea()) {
    actionChildren.push({
      title: 'Tạo mới',
      key: 'create-new',
      url: '/kaizen/actions/create-new',
      isRoot: false,
    });
  }

  if (canCreateIdea() || canExportReport()) {
    actionChildren.push({
      title: 'Truy vấn',
      key: 'query',
      url: '/kaizen/actions/query',
      isRoot: false,
    });
  }

  if (canViewMyTasks()) {
    actionChildren.push({
      title: 'My Tasks',
      key: 'my-tasks',
      url: '/kaizen/actions/my-tasks',
      isRoot: false,
    });
  }

  const sidebar: SidebarNavigationItem[] = [];

  sidebar.push({
    title: 'Dashboard',
    key: 'dashboard',
    url: '/kaizen/dashboard',
    icon: <DashboardOutlined />,
    isRoot: false,
  });

  if (actionChildren.length > 0) {
    sidebar.push({
      title: 'Hành động',
      key: 'actions',
      icon: <BuildOutlined />,
      children: actionChildren,
      isRoot: false,
    });
  }

  if (canManageData()) {
    sidebar.push({
      title: 'Quản lý nhân viên',
      key: 'employee-management',
      url: '/kaizen/employee-management',
      icon: <UserOutlined />,
      isRoot: true,
    });

    sidebar.push({
      title: 'Quản lý tài khoản',
      key: 'account-management',
      url: '/kaizen/account-management',
      icon: <UserOutlined />,
      isRoot: true,
    });
  }

  return sidebar;
};

export const sidebarNavigation = (factory: string): SidebarNavigationItem[] => {
  switch (factory) {
    case 'wh':
      return sidebarWH;
    case 'cbm':
      return sidebarCBM;
    case 'iqc':
      return sidebarIQC;
    case 'fac':
      return sidebarFac;
    case 'int':
      return sidebarINT;
    case 'spa':
      return sidebarSPA;
    case 'calib':
      return getSidebarCalib();
    case 'kaizen':
      return getSidebarKPI();
    default:
      return [];
  }
};

// [
//   {
//     title: 'common.gemba-dashboard',
//     key: 'index',
//     url: '/' + factory + '/dashboard',
//     icon: <DashboardOutlined />,
//     isRoot: false,
//   },
//   {
//     title: 'common.issues',
//     key: 'forms',
//     icon: <BugOutlined />,
//     children: [
//       {
//         title: 'issuepage.createUpdateIssue',
//         key: 'issue-forms',
//         url: '/' + factory + '/issue/info',
//         isRoot: false,
//       },
//       {
//         title: 'common.queryissue',
//         key: 'issue-query',
//         url: '/' + factory + '/issue/query',
//         isRoot: false,
//       },
//       {
//         title: 'common.handleIssue',
//         key: 'issue-handle',
//         url: '/' + factory + '/issue/handle',
//         isRoot: false,
//       },
//     ],
//     isRoot: false,
//   },
//   {
//     title: 'common.charts',
//     key: 'charts',
//     url: '/' + factory + '/charts',
//     icon: <LineChartOutlined />,
//     isRoot: false,
//   },
//   {
//     title: 'common.issueOwner',
//     key: 'issueOwner',
//     url: '/' + factory + '/issueOwner',
//     icon: <CompassOutlined />,
//     isRoot: true,
//   },
//   {
//     title: 'common.usersManagement',
//     key: 'usermanagement',
//     url: '/' + factory + '/users',
//     icon: <UserOutlined />,
//     isRoot: true,
//   },
//   {
//     title: 'common.stagesManagement',
//     key: 'stagemanagement',
//     url: '/' + factory + '/stages',
//     icon: <BuildOutlined />,
//     isRoot: true,
//   },
//   {
//     title: 'common.equipmentManagement',
//     key: 'equipments',
//     url: '/' + factory + '/equipments',
//     icon: <ApiOutlined />,
//     isRoot: false,
//   },
//   {
//     title: 'common.productManagement',
//     key: 'products',
//     url: '/' + factory + '/products',
//     icon: <BarcodeOutlined />,
//     isRoot: true,
//   },
//   {
//     title: 'common.qualityCodeManagement',
//     key: 'qualityCode',
//     url: '/' + factory + '/quality-codes',
//     icon: <ExceptionOutlined />,
//     isRoot: true,
//   },
// ];
